import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getFinancialYear = (transactionDate) => {
  if (!transactionDate) return '';
  
  const date = new Date(transactionDate);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  
  if (month >= 1 && month <= 3) {
    return `${year-1}-${year}`;
  } else {
    return `${year}-${year+1}`;
  }
};

// Validation functions
const validateTAN = (tan) => {
  // TAN format: 4 letters + 5 digits + 1 letter (e.g., ABCD12345E)
  const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;
  return tanRegex.test(tan.toUpperCase());
};

const validatePositiveNumber = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

const formatTAN = (value) => {
  // Remove any non-alphanumeric characters and convert to uppercase
  return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
};

const TdsTcsPage = ({employee_id, financial_year }) => {

  const [tdsEntries, setTdsEntries] = useState([]);
  const [tdsSection, setTdsSection] = useState('');
  const [deductorName, setDeductorName] = useState('');
  const [deductorAddress, setDeductorAddress] = useState('');
  const [deductorTan, setDeductorTan] = useState('');
  const [taxDeducted, setTaxDeducted] = useState('');
  const [incomeReceived, setIncomeReceived] = useState('');
  const [tdsTransactionDate, setTdsTransactionDate] = useState('');
  const [tdsFinancialYear, setTdsFinancialYear] = useState(financial_year || "");
  const [tdsEmployeeId, setTdsEmployeeId] = useState(employee_id || "");
  const [editingTdsId, setEditingTdsId] = useState(null);

  const [tcsEntries, setTcsEntries] = useState([]);
  const [tcsSection, setTcsSection] = useState('');
  const [collectorName, setCollectorName] = useState('');
  const [collectorAddress, setCollectorAddress] = useState('');
  const [collectorTan, setCollectorTan] = useState('');
  const [taxCollected, setTaxCollected] = useState('');
  const [tcsTransactionDate, setTcsTransactionDate] = useState('');
  const [tcsFinancialYear, setTcsFinancialYear] = useState(financial_year);
  const [tcsEmployeeId, setTcsEmployeeId] = useState(employee_id ||"");
  const [editingTcsId, setEditingTcsId] = useState(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    tds: {
      deductorTan: '',
      taxDeducted: '',
      incomeReceived: ''
    },
    tcs: {
      collectorTan: '',
      taxCollected: ''
    }
  });

  const [tdsSummary, setTdsSummary] = useState({
    totalEntries: 0,
    totalTaxDeducted: 0,
    totalIncome: 0
  });
  const [tcsSummary, setTcsSummary] = useState({
    totalEntries: 0,
    totalTaxCollected: 0
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState({
    tds: false,
    tcs: false,
    summary: false
  });

  const financialYearOptions = [
    { value: '2024-25', label: '2024-25' },
    { value: '2023-24', label: '2023-24' },
    { value: '2022-23', label: '2022-23' },
    { value: '2021-22', label: '2021-22' },
    { value: '2020-21', label: '2020-21' }
  ];

  const tdsSectionOptions = [
    { value: 'Section 192', label: 'Section 192 - Salary' },
    { value: 'Section 194A', label: 'Section 194A - Interest other than interest on securities' },
    { value: 'Section 194C', label: 'Section 194C - Payment to contractors' },
    { value: 'Section 194I', label: 'Section 194I - Rent' },
    { value: 'Section 194J', label: 'Section 194J - Professional services' }
  ];

  const tcsSectionOptions = [
    { value: 'Section 206C(1)', label: 'Section 206C(1) - Sale of goods' },
    { value: 'Section 206C(1C)', label: 'Section 206C(1C) - Lease or license' },
    { value: 'Section 206C(1F)', label: 'Section 206C(1F) - Motor vehicle' },
    { value: 'Section 206C(1H)', label: 'Section 206C(1H) - Sale of goods' }
  ];

  useEffect(() => {
    if (employee_id) {
      fetchTdsEntries(employee_id);
      fetchTcsEntries(employee_id);
      fetchSummaries(employee_id);
      
      setTdsEmployeeId(employee_id);
      setTcsEmployeeId(employee_id);
    }
  }, [employee_id]);

  useEffect(() => {
    if (tdsTransactionDate) {
      setTdsFinancialYear(getFinancialYear(tdsTransactionDate));
    }
  }, [tdsTransactionDate]);

  useEffect(() => {
    if (tcsTransactionDate) {
      setTcsFinancialYear(getFinancialYear(tcsTransactionDate));
    }
  }, [tcsTransactionDate]);

  // Validation helper functions
  const validateTdsForm = () => {
    const errors = {
      deductorTan: '',
      taxDeducted: '',
      incomeReceived: ''
    };

    if (deductorTan && !validateTAN(deductorTan)) {
      errors.deductorTan = 'Invalid TAN format. Should be 4 letters + 5 digits + 1 letter (e.g., ABCD12345E)';
    }

    if (taxDeducted && !validatePositiveNumber(taxDeducted)) {
      errors.taxDeducted = 'Tax deducted must be a positive number';
    }

    if (incomeReceived && !validatePositiveNumber(incomeReceived)) {
      errors.incomeReceived = 'Income received must be a positive number';
    }

    setValidationErrors(prev => ({ ...prev, tds: errors }));
    return !Object.values(errors).some(error => error !== '');
  };

  const validateTcsForm = () => {
    const errors = {
      collectorTan: '',
      taxCollected: ''
    };

    if (collectorTan && !validateTAN(collectorTan)) {
      errors.collectorTan = 'Invalid TAN format. Should be 4 letters + 5 digits + 1 letter (e.g., ABCD12345E)';
    }

    if (taxCollected && !validatePositiveNumber(taxCollected)) {
      errors.taxCollected = 'Tax collected must be a positive number';
    }

    setValidationErrors(prev => ({ ...prev, tcs: errors }));
    return !Object.values(errors).some(error => error !== '');
  };

  // Input handlers with validation
  const handleTdsNumberChange = (field, value) => {
    if (value === '' || validatePositiveNumber(value)) {
      if (field === 'taxDeducted') {
        setTaxDeducted(value);
      } else if (field === 'incomeReceived') {
        setIncomeReceived(value);
      }
    }
  };

  const handleTcsNumberChange = (field, value) => {
    if (value === '' || validatePositiveNumber(value)) {
      if (field === 'taxCollected') {
        setTaxCollected(value);
      }
    }
  };

  const handleTanChange = (field, value) => {
    const formattedValue = formatTAN(value);
    if (formattedValue.length <= 10) {
      if (field === 'deductorTan') {
        setDeductorTan(formattedValue);
      } else if (field === 'collectorTan') {
        setCollectorTan(formattedValue);
      }
    }
  };

  const fetchTdsEntries = async (empId) => {
    try {
               const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(prev => ({ ...prev, tds: true }));
      const response = await axios.get(`${API_BASE_URL}/api/employees/${empId}/tds`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      if (response.data && response.data.data) {
        setTdsEntries(response.data.data);
      } else {
        setTdsEntries([]);
      }
    } catch (error) {
      //console.error('Error fetching TDS entries:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch TDS entries';
      showSnackbar(errorMessage, 'error');
      setTdsEntries([]); 
    } finally {
      setLoading(prev => ({ ...prev, tds: false }));
    }
  };

  const fetchTcsEntries = async (empId) => {
    try {
               const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(prev => ({ ...prev, tcs: true }));
      const response = await axios.get(`${API_BASE_URL}/api/employees/${empId}/tcs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
        
          }
        }
      );
      if (response.data && response.data.data) {
        setTcsEntries(response.data.data);
      } else {
        setTcsEntries([]);
      }
    } catch (error) {
      //console.error('Error fetching TCS entries:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch TCS entries';
      showSnackbar(errorMessage, 'error');
      setTcsEntries([]); 
    } finally {
      setLoading(prev => ({ ...prev, tcs: false }));
    }
  };

  const fetchSummaries = async (empId) => {
    try {
               const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(prev => ({ ...prev, summary: true }));
      
      const fetchTdsSummary = axios.get(`${API_BASE_URL}/api/employees/${empId}/tds-summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      )
        .catch(error => {
          //console.error('Error fetching TDS summary:', error);
          return { data: { totalEntries: 0, totalTaxDeducted: 0, totalIncome: 0 } };
        });
      
      const fetchTcsSummary = axios.get(`${API_BASE_URL}/api/employees/${empId}/tcs-summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
         
        }
      })
        .catch(error => {
          //console.error('Error fetching TCS summary:', error);
          return { data: { totalEntries: 0, totalTaxCollected: 0 } };
        });
      
      const [tdsResponse, tcsResponse] = await Promise.all([fetchTdsSummary, fetchTcsSummary]);
      
      setTdsSummary(tdsResponse.data);
      setTcsSummary(tcsResponse.data);
    } catch (error) {
      //console.error('Error fetching summaries:', error);
      showSnackbar('Failed to fetch tax summaries', 'error');
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  };

  const tooltips = {
    employeeId: "Employee ID associated with this tax entry",
    financialYear: "Financial year of the tax entry (e.g., 2024-25)",
    tdsSection: "Select the appropriate TDS section under which tax was deducted",
    deductorName: "Enter the name of the person/entity who deducted tax",
    deductorAddress: "Enter the full address of the deductor",
    deductorTan: "Enter the 10-character Tax Deduction Account Number (TAN) of the deductor (Format: ABCD12345E)",
    taxDeducted: "Enter the amount of tax deducted in rupees (must be positive)",
    incomeReceived: "Enter the gross amount of income received before tax deduction (must be positive)",
    tdsTransactionDate: "Enter the date when the TDS was deducted",
    
    tcsSection: "Select the appropriate TCS section under which tax was collected",
    collectorName: "Enter the name of the person/entity who collected tax",
    collectorAddress: "Enter the full address of the collector",
    collectorTan: "Enter the 10-character Tax Collection Account Number (TAN) of the collector (Format: ABCD12345E)",
    taxCollected: "Enter the amount of tax collected in rupees (must be positive)",
    tcsTransactionDate: "Enter the date when the TCS was collected"
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAddTdsRow = async () => {
    if (!tdsSection || !deductorName || !deductorTan || !taxDeducted || !tdsEmployeeId) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    if (!validateTdsForm()) {
      showSnackbar('Please fix validation errors before submitting', 'error');
      return;
    }
   
    try {
      setLoading(prev => ({ ...prev, tds: true }));
      const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      const tdsData = {
        employee_id: tdsEmployeeId,
        section: tdsSection,
        deductorName,
        deductorAddress,
        deductorTan,
        taxDeducted: parseFloat(taxDeducted),
        incomeReceived: incomeReceived ? parseFloat(incomeReceived) : 0,
        transactionDate: tdsTransactionDate,
        financialYear: tdsFinancialYear
      };

      if (editingTdsId) {
        await axios.put(`${API_BASE_URL}/api/employees/${tdsEmployeeId}/tds/${editingTdsId}`, tdsData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        showSnackbar('TDS entry updated successfully');
        setEditingTdsId(null);
      } else {
        await axios.post(`${API_BASE_URL}/api/employees/${tdsEmployeeId}/tds`, tdsData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        showSnackbar('TDS entry added successfully');
      }
      
      await fetchTdsEntries(tdsEmployeeId);
      await fetchSummaries(tdsEmployeeId);
      
      resetTdsForm();
    } catch (error) {
      //console.error('Error saving TDS entry:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save TDS entry';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(prev => ({ ...prev, tds: false }));
    }
  };

  const handleDeleteTdsEntry = async (id) => {
    if (!id) {
      showSnackbar('Invalid entry ID', 'error');
      return;
    }
    
    try {
               const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(prev => ({ ...prev, tds: true }));
      await axios.delete(`${API_BASE_URL}/api/employees/${tdsEmployeeId}/tds/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
     
      await fetchTdsEntries(tdsEmployeeId);
      await fetchSummaries(tdsEmployeeId);
      
      showSnackbar('TDS entry deleted successfully', 'info');
    } catch (error) {
      //console.error('Error deleting TDS entry:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete TDS entry';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(prev => ({ ...prev, tds: false }));
    }
  };

  const handleEditTdsEntry = (entry) => {
    if (!entry) {
      showSnackbar('Invalid entry to edit', 'error');
      return;
    }
    
    setTdsEmployeeId(entry.employee_id);
    setTdsSection(entry.section || '');
    setDeductorName(entry.deductorName || '');
    setDeductorAddress(entry.deductorAddress || '');
    setDeductorTan(entry.deductorTan || '');
    setTaxDeducted(entry.taxDeducted ? entry.taxDeducted.toString() : '');
    setIncomeReceived(entry.incomeReceived ? entry.incomeReceived.toString() : '');
    setTdsTransactionDate(entry.transactionDate || '');
    setTdsFinancialYear(entry.financialYear || '');
    setEditingTdsId(entry.id);
    
    showSnackbar('Ready to edit TDS entry', 'info');
  };

  const resetTdsForm = () => {
    setTdsSection('');
    setDeductorName('');
    setDeductorAddress('');
    setDeductorTan('');
    setTaxDeducted('');
    setIncomeReceived('');
    setTdsTransactionDate('');
    setTdsFinancialYear('');
    setEditingTdsId(null);
    setValidationErrors(prev => ({ ...prev, tds: { deductorTan: '', taxDeducted: '', incomeReceived: '' } }));
  };

  const handleAddTcsRow = async () => {
    if (!tcsSection || !collectorName || !collectorTan || !taxCollected || !tcsEmployeeId) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    if (!validateTcsForm()) {
      showSnackbar('Please fix validation errors before submitting', 'error');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, tcs: true }));
       const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      const tcsData = {
        employee_id: tcsEmployeeId,
        section: tcsSection,
        collectorName,
        collectorAddress,
        collectorTan,
        taxCollected: parseFloat(taxCollected),
        transactionDate: tcsTransactionDate,
        financialYear: tcsFinancialYear
      };

      if (editingTcsId) {
        await axios.put(`${API_BASE_URL}/api/employees/${tcsEmployeeId}/tcs/${editingTcsId}`, tcsData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        showSnackbar('TCS entry updated successfully');
        setEditingTcsId(null);
      } else {
        await axios.post(`${API_BASE_URL}/api/employees/${tcsEmployeeId}/tcs`, tcsData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        showSnackbar('TCS entry added successfully');
      }
      
      await fetchTcsEntries(tcsEmployeeId);
      await fetchSummaries(tcsEmployeeId);
     
      resetTcsForm();
    } catch (error) {
      //console.error('Error saving TCS entry:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save TCS entry';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(prev => ({ ...prev, tcs: false }));
    }
  };

  const handleDeleteTcsEntry = async (id) => {
    if (!id) {
      showSnackbar('Invalid entry ID', 'error');
      return;
    }
    
    try {
               const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(prev => ({ ...prev, tcs: true }));
      await axios.delete(`${API_BASE_URL}/api/employees/${tcsEmployeeId}/tcs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      await fetchTcsEntries(tcsEmployeeId);
      await fetchSummaries(tcsEmployeeId);
      
      showSnackbar('TCS entry deleted successfully', 'info');
    } catch (error) {
      //console.error('Error deleting TCS entry:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete TCS entry';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(prev => ({ ...prev, tcs: false }));
    }
  };

  const handleEditTcsEntry = (entry) => {
    if (!entry) {
      showSnackbar('Invalid entry to edit', 'error');
      return;
    }
    
    setTcsEmployeeId(entry.employee_id);
    setTcsSection(entry.section || '');
    setCollectorName(entry.collectorName || '');
    setCollectorAddress(entry.collectorAddress || '');
    setCollectorTan(entry.collectorTan || '');
    setTaxCollected(entry.taxCollected ? entry.taxCollected.toString() : '');
    setTcsTransactionDate(entry.transactionDate || '');
    setTcsFinancialYear(entry.financialYear || '');
    setEditingTcsId(entry.id);
    
    showSnackbar('Ready to edit TCS entry', 'info');
  };

  const resetTcsForm = () => {
    setTcsSection('');
    setCollectorName('');
    setCollectorAddress('');
    setCollectorTan('');
    setTaxCollected('');
    setTcsTransactionDate('');
    setTcsFinancialYear('');
    setEditingTcsId(null);
    setValidationErrors(prev => ({ ...prev, tcs: { collectorTan: '', taxCollected: '' } }));
  };

  const handleRefreshData = () => {
    if (employee_id) {
      fetchTdsEntries(employee_id);
      fetchTcsEntries(employee_id);
      fetchSummaries(employee_id);
      showSnackbar('Data refreshed', 'info');
    } else {
      showSnackbar('Employee ID is required to refresh data', 'warning');
    }
  };

  const totalTdsAmount = tdsEntries.reduce((sum, entry) => sum + Number(entry.taxDeducted || 0), 0);
  const totalTcsAmount = tcsEntries.reduce((sum, entry) => sum + Number(entry.taxCollected || 0), 0);
  const totalIncome = tdsEntries.reduce((sum, entry) => sum + Number(entry.incomeReceived || 0), 0);

  return (
    <Container maxWidth="1500px" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
        }}
      >
        <Box sx={{ mt: 4, mb: 6 }}>
          <Typography 
            variant="h5" gutterBottom justifyContent="center" align="center" 
          >
            Details of Tax Deducted at Source (TDS)
          </Typography>
          
          <Paper elevation={2} sx={{ p: 3, borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>Add New TDS Entry</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.employeeId} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="tds-employee-id"
                    label="Employee ID"
                    value={tdsEmployeeId}
                    onChange={(e) => setTdsEmployeeId(e.target.value)}
                    InputProps={{ readOnly: true }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.financialYear} placement="top" arrow>
                  <FormControl fullWidth>
                    <TextField
                      id="tds-financial-year"
                      label="Financial Year"
                      value={tdsFinancialYear}
                      onChange={(e) => setTdsFinancialYear(e.target.value)}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.tdsSection} placement="top" arrow>
                  <FormControl fullWidth required>
                    <InputLabel id="tds-section-label">TDS Section</InputLabel>
                    <Select
                      labelId="tds-section-label"
                      id="tds-section"
                      value={tdsSection}
                      label="TDS Section"
                      onChange={(e) => setTdsSection(e.target.value)}
                    >
                      {tdsSectionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.deductorName} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="deductor-name"
                    label="Deductor Name"
                    value={deductorName}
                    onChange={(e) => setDeductorName(e.target.value)}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.deductorTan} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="deductor-tan"
                    label="TAN of Deductor"
                    value={deductorTan}
                    onChange={(e) => handleTanChange('deductorTan', e.target.value)}
                    error={!!validationErrors.tds.deductorTan}
                    helperText={validationErrors.tds.deductorTan}
                    placeholder="ABCD12345E"
                    inputProps={{ maxLength: 10 }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.deductorAddress} placement="top" arrow>
                  <TextField
                    fullWidth
                    id="deductor-address"
                    label="Address of Deductor"
                    multiline
                    rows={2}
                    value={deductorAddress}
                    onChange={(e) => setDeductorAddress(e.target.value)}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.taxDeducted} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="tax-deducted"
                    label="Tax Deducted"
                    type="number"
                    value={taxDeducted}
                    onChange={(e) => handleTdsNumberChange('taxDeducted', e.target.value)}
                    error={!!validationErrors.tds.taxDeducted}
                    helperText={validationErrors.tds.taxDeducted}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.incomeReceived} placement="top" arrow>
                  <TextField
                    fullWidth
                    id="income-received"
                    label="Income Received"
                    type="number"
                    value={incomeReceived}
                    onChange={(e) => handleTdsNumberChange('incomeReceived', e.target.value)}
                    error={!!validationErrors.tds.incomeReceived}
                    helperText={validationErrors.tds.incomeReceived}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.tdsTransactionDate} placement="top" arrow>
                  <TextField
                    fullWidth
                    id="tds-transaction-date"
                    label="Transaction Date"
                    type="date"
                    value={tdsTransactionDate}
                    onChange={(e) => setTdsTransactionDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddTdsRow}
                  sx={{ mt: 1 }}
                  disabled={loading.tds}
                >
                  {loading.tds ? <CircularProgress size={20} /> : (editingTdsId ? 'Update TDS Entry' : 'Add TDS Entry')}
                </Button>
                {editingTdsId && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={resetTdsForm}
                    sx={{ mt: 1, ml: 2 }}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>
        
        {/* <Divider sx={{ my: 4 }} />
        
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h3">
              TDS Entries ({tdsEntries.length})
            </Typography>
            <IconButton
              onClick={handleRefreshData}
              color="primary"
              disabled={loading.tds}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          
          {loading.tds ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table sx={{ minWidth: 650 }} aria-label="TDS entries table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Section</TableCell>
                    <TableCell>Deductor Name</TableCell>
                    <TableCell>TAN</TableCell>
                    <TableCell align="right">Tax Deducted</TableCell>
                    <TableCell align="right">Income Received</TableCell>
                    <TableCell>Transaction Date</TableCell>
                    <TableCell>Financial Year</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tdsEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No TDS entries found. Add your first entry above.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tdsEntries.map((entry) => (
                      <TableRow 
                        key={entry.id}
                        sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                      >
                        <TableCell>{entry.section}</TableCell>
                        <TableCell>{entry.deductorName}</TableCell>
                        <TableCell>{entry.deductorTan}</TableCell>
                        <TableCell align="right">₹{Number(entry.taxDeducted || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">₹{Number(entry.incomeReceived || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          {entry.transactionDate ? new Date(entry.transactionDate).toLocaleDateString('en-IN') : '-'}
                        </TableCell>
                        <TableCell>{entry.financialYear}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTdsEntry(entry)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTdsEntry(entry.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {tdsEntries.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ₹{totalTdsAmount.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ₹{totalIncome.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box> */}

        <Divider sx={{ my: 6 }} />

        {/* TCS Section */}
        <Box sx={{ mt: 6, mb: 6 }}>
          <Typography 
         variant="h5" gutterBottom justifyContent="center" align="center" 
          >
            Details of Tax Collected at Source (TCS)
          </Typography>
          
          <Paper elevation={2} sx={{ p: 3, borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>Add New TCS Entry</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.employeeId} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="tcs-employee-id"
                    label="Employee ID"
                    value={tcsEmployeeId}
                    onChange={(e) => setTcsEmployeeId(e.target.value)}
                    InputProps={{ readOnly: true }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.financialYear} placement="top" arrow>
                  <FormControl fullWidth>
                    <TextField
                      id="tcs-financial-year"
                      label="Financial Year"
                      value={tcsFinancialYear}
                      onChange={(e) => setTcsFinancialYear(e.target.value)}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.tcsSection} placement="top" arrow>
                  <FormControl fullWidth required>
                    <InputLabel id="tcs-section-label">TCS Section</InputLabel>
                    <Select
                      labelId="tcs-section-label"
                      id="tcs-section"
                      value={tcsSection}
                      label="TCS Section"
                      onChange={(e) => setTcsSection(e.target.value)}
                    >
                      {tcsSectionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.collectorName} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="collector-name"
                    label="Collector Name"
                    value={collectorName}
                    onChange={(e) => setCollectorName(e.target.value)}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.collectorTan} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="collector-tan"
                    label="TAN of Collector"
                    value={collectorTan}
                    onChange={(e) => handleTanChange('collectorTan', e.target.value)}
                    error={!!validationErrors.tcs.collectorTan}
                    helperText={validationErrors.tcs.collectorTan}
                    placeholder="ABCD12345E"
                    inputProps={{ maxLength: 10 }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.collectorAddress} placement="top" arrow>
                  <TextField
                    fullWidth
                    id="collector-address"
                    label="Address of Collector"
                    multiline
                    rows={2}
                    value={collectorAddress}
                    onChange={(e) => setCollectorAddress(e.target.value)}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.taxCollected} placement="top" arrow>
                  <TextField
                    required
                    fullWidth
                    id="tax-collected"
                    label="Tax Collected"
                    type="number"
                    value={taxCollected}
                    onChange={(e) => handleTcsNumberChange('taxCollected', e.target.value)}
                    error={!!validationErrors.tcs.taxCollected}
                    helperText={validationErrors.tcs.taxCollected}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Tooltip title={tooltips.tcsTransactionDate} placement="top" arrow>
                  <TextField
                    fullWidth
                    id="tcs-transaction-date"
                    label="Transaction Date"
                    type="date"
                    value={tcsTransactionDate}
                    onChange={(e) => setTcsTransactionDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddTcsRow}
                  sx={{ mt: 1 }}
                  disabled={loading.tcs}
                >
                  {loading.tcs ? <CircularProgress size={20} /> : (editingTcsId ? 'Update TCS Entry' : 'Add TCS Entry')}
                </Button>
                {editingTcsId && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={resetTcsForm}
                    sx={{ mt: 1, ml: 2 }}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h3">
              TCS Entries ({tcsEntries.length})
            </Typography>
            <IconButton
              onClick={handleRefreshData}
              color="primary"
              disabled={loading.tcs}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          
          {loading.tcs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table sx={{ minWidth: 650 }} aria-label="TCS entries table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Section</TableCell>
                    <TableCell>Collector Name</TableCell>
                    <TableCell>TAN</TableCell>
                    <TableCell align="right">Tax Collected</TableCell>
                    <TableCell>Transaction Date</TableCell>
                    <TableCell>Financial Year</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tcsEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No TCS entries found. Add your first entry above.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tcsEntries.map((entry) => (
                      <TableRow 
                        key={entry.id}
                        sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                      >
                        <TableCell>{entry.section}</TableCell>
                        <TableCell>{entry.collectorName}</TableCell>
                        <TableCell>{entry.collectorTan}</TableCell>
                        <TableCell align="right">₹{Number(entry.taxCollected || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          {entry.transactionDate ? new Date(entry.transactionDate).toLocaleDateString('en-IN') : '-'}
                        </TableCell>
                        <TableCell>{entry.financialYear}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTcsEntry(entry)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTcsEntry(entry.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {tcsEntries.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#e8f5e8' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ₹{totalTcsAmount.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box> */}

        {/* Summary Section */}
        {/* <Divider sx={{ my: 6 }} />
        
        <Box sx={{ mt: 6 }}>
          <Typography variant="h6" gutterBottom>
            Tax Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    TDS Summary
                  </Typography>
                  {loading.summary ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      <Typography variant="body2">
                        Total Entries: {tdsSummary.totalEntries || tdsEntries.length}
                      </Typography>
                      <Typography variant="body2">
                        Total Tax Deducted: ₹{(tdsSummary.totalTaxDeducted || totalTdsAmount).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2">
                        Total Income: ₹{(tdsSummary.totalIncome || totalIncome).toLocaleString('en-IN')}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="secondary" gutterBottom>
                    TCS Summary
                  </Typography>
                  {loading.summary ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      <Typography variant="body2">
                        Total Entries: {tcsSummary.totalEntries || tcsEntries.length}
                      </Typography>
                      <Typography variant="body2">
                        Total Tax Collected: ₹{(tcsSummary.totalTaxCollected || totalTcsAmount).toLocaleString('en-IN')}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box> */}

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbarOpen} 
          autoHideDuration={6000} 
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbarSeverity} 
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default TdsTcsPage;